import { stringify, parse } from 'query-string'
import { compile, PathFunction } from 'path-to-regexp'

export enum Route {
  /*
   * Internal routes for application
   */
  Help = 'Help',
  Article = 'Article',
  ArticleTop = 'ArticleTop',
  Categories = 'Categories',
  /*
   * External routes for application point to external apps
   */
  GetHelp = 'GetHelp',
  ContactUs = 'ContactUs',
  GetHelpOrderDelivery = 'GetHelpOrderDelivery',
  GetHelpOrderIngredients = 'GetHelpOrderIngredients',
  GetHelpOrderRecipeCards = 'GetHelpOrderRecipeCards',
  Login = 'Login',
}

type NoParams = Record<string, never>

const routes = {
  /*
   * Internal routes for application
   */
  [Route.Help]: compile<NoParams>('/'),
  [Route.Article]: compile<{
    articleId: string,
  }>('/article/:articleId'),
  [Route.ArticleTop]: compile<NoParams>('/article/top'),
  [Route.Categories]: compile<{
    categoryId: string,
  }>('/categories/:categoryId'),
  /*
   * External routes for application point to external apps
   */
  [Route.GetHelp]: compile<NoParams>('/get-help'),
  [Route.ContactUs]: compile<NoParams>('/get-help/contact'),
  [Route.GetHelpOrderDelivery]: compile<{
    userId: string,
    orderId: string,
  }>('/get-help/user/:userId/order/:orderId/delivery'),
  [Route.GetHelpOrderIngredients]: compile<{
    userId: string,
    orderId: string,
  }>('/get-help/user/:userId/order/:orderId/ingredients'),
  [Route.GetHelpOrderRecipeCards]: compile<{
    userId: string,
    orderId: string,
  }>('/get-help/user/:userId/order/:orderId/recipe-cards'),
  // TODO: Login has not been implemented yet
  [Route.Login]: compile<NoParams>('/'),
}

/*
 * This is mapping of routes that inside this application
 * and those that are external to this application
 */
const externalRoutes: Record<Route, boolean> = {
  /*
   * Internal routes for application
   */
  [Route.Help]: false,
  [Route.Article]: false,
  [Route.ArticleTop]: false,
  [Route.Categories]: false,
  /*
   * External routes for application point to external apps
   */
  [Route.GetHelp]: true,
  [Route.ContactUs]: true,
  [Route.GetHelpOrderDelivery]: true,
  [Route.GetHelpOrderIngredients]: true,
  [Route.GetHelpOrderRecipeCards]: true,
  [Route.Login]: true,
}

export type RouteParams<R extends Route> =
  (typeof routes)[R] extends PathFunction<infer P> ? P : undefined

/**
 * Returns a string that is the relative path for a route.
 *
 * ```typescript
 * const relativeHref = getRoute(Route.Blog, { slug: 'suppliers' });
 * ```
 * output:
 * ```text
 * /blog/suppliers
 * ```
 */
export const getRoute = <R extends Route>(route: R, params?: RouteParams<R>) => (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  routes[route](params as any)
)

/**
 * Returns a boolean if route is an external route outside of this app
 *
 * ```typescript
 * const isExternalBool = isExternal();
 *
 * if (isExternalBool) redirect()
 * ```
 */
export const isExternal = (route: Route) => externalRoutes[route]

export interface RouteURLParams<R extends Route> {
  route: R;
  routeParams?: RouteParams<R>;
  absolute?: boolean;
  queryParams?: { [key: string]: string };
}

/**
 * This methods provides the base site;
 * * in production `https://gousto.co.uk/`
 * * in develop `http://frontend.gousto.local:3000/`
 *
 * based on the route.
 * ```typescript
 * const queryParamsForBlog = getBaseSite();
 * ```
 */
const getBaseSite = () => `${process.env.REACT_APP_GOUSTO_MAIN_URL}`

/**
 * This methods checks if route is same as the current route.
 *
 * if they match:
 *  * merges new params with existing params
 *
 * else:
 *  * use only the new params.
 *
 * // TODO: This method only works client-side
 *
 * ```typescript
 * const queryParamsForBlog = getQueryParams({
 *  route: Route.Blog,
 *  routeParams: { slug: 'get_blog' },
 *  queryParams: { utm_source: 'menu', utm_id: '123' },
 * });
 * ```
 */
const getQueryParams = <R extends Route>({
  route,
  routeParams,
  queryParams,
}: RouteURLParams<R>) => {
  const { pathname, search } = global?.window?.location || {}
  const newPath = getRoute<R>(route, routeParams)
  const routeMatch = pathname === newPath

  if (!routeMatch) {
    return queryParams
  }
  const existingQueryParams = parse(search)

  return { ...existingQueryParams, ...queryParams }
}

/**
 * This method returns either the relative URL if the
 * route is in this application or the absolute URL if
 * the route is external.
 *
 * ```typescript
 * const url = getRouteURL({
 *  route: Route.Blog,
 *  routeParams: { slug: 'get_blog' },
 *  queryParams: { utm_source: 'menu', utm_id: '123' },
 *  absolute: false,
 * });
 * ```
 * output:
 * ```text
 * https://www.gousto.co.uk/blog/get_block?utm_source=menu&utm_id=123
 * ```
 */
export const getRouteURL = <R extends Route>({
  route,
  routeParams,
  queryParams,
  absolute,
}: RouteURLParams<R>) => {
  // omits empty query params passed
  const allQueryParams = getQueryParams({ route, routeParams, queryParams }) || {}
  const hasQueryParams = Object.entries(allQueryParams).length !== 0
  const query = hasQueryParams ? `?${stringify(allQueryParams)}` : ''
  const href = getRoute(route, routeParams) + query
  const site = getBaseSite()
  const url = absolute ? site + href : href

  return url
}
